import { z } from 'zod';
import { QuestionId } from './common.js';
import { QuestionType } from './questionnaire.js';

export const MAX_TEXT = 2000;
export const MAX_OTHER = 200;

/** What the browser sends on every change (autosave). The server fills questionVersion and type from the bank. */
export const AnswerInput = z.object({
  selected: z.array(z.string().max(80)).max(20).default([]),
  /** Text typed into the 其他 / Other box (on a choice question, or a compound part's 其他 such as C3's department). */
  other: z.string().max(MAX_OTHER).optional(),
  /** Free text (A1b, B4, I3) or the optional "say more" line (D1, E3). */
  text: z.string().max(MAX_TEXT).optional(),
  scale: z.number().int().min(1).max(5).optional(),
  /** I1: first choice, then the next two, as option ids. */
  rank: z.array(z.string().max(80)).max(3).optional(),
  /** Compound questions (C3): part id -> option id. */
  parts: z.record(z.string().max(20), z.string().max(80)).optional(),
  unsure: z.boolean().default(false),
  skipped: z.boolean().default(false),
  /** Time on the screen, for drop-off and median-time analytics. */
  elapsedMs: z.number().int().min(0).max(24 * 3600 * 1000).optional(),
});
export type AnswerInput = z.infer<typeof AnswerInput>;
export type AnswerInputRaw = z.input<typeof AnswerInput>;

export const Answer = AnswerInput.extend({
  questionId: QuestionId,
  questionVersion: z.number().int().positive(),
  type: QuestionType,
  updatedAt: z.string().optional(),
});
export type Answer = z.infer<typeof Answer>;

/** I4. Stored encrypted on the session, never in answers and never sent to the model. */
export const ContactInput = z
  .object({
    name: z.string().trim().max(60).optional(),
    company: z.string().trim().max(120).optional(),
    phoneOrWechat: z.string().trim().max(60).optional(),
    email: z.union([z.literal(''), z.email().max(200)]).optional(),
    bestTime: z.string().trim().max(60).optional(),
  })
  .strict();
export type ContactInput = z.infer<typeof ContactInput>;

/** I5. A separate, explicit consent for contact, distinct from the data-processing consent at the start. */
export const ContactConsentInput = z.object({ agree: z.boolean() }).strict();
export type ContactConsentInput = z.infer<typeof ContactConsentInput>;

export type AnswerMap = Record<string, Answer | undefined>;

/** Answered means the respondent gave something, including "not sure"; a skip is recorded but does not count. */
export const isAnswered = (a: Answer | AnswerInput | undefined): boolean => {
  if (!a || a.skipped) return false;
  if (a.unsure) return true;
  return (
    a.selected.length > 0 ||
    !!a.text?.trim() ||
    a.scale !== undefined ||
    (a.rank?.length ?? 0) > 0 ||
    Object.keys(a.parts ?? {}).length > 0 ||
    !!a.other?.trim()
  );
};
